import type { LucideIcon } from 'lucide-react';
import {
  Activity,
  Bookmark,
  BookOpen,
  CircleDot,
  FileText,
  Handshake,
  Heart,
  MessageCircle,
  MessageSquareReply,
  PencilLine,
  Rss,
  Share2,
  ShieldCheck,
  Users,
} from 'lucide-react';
import { PRIVATE_URIS } from './privateURIs.constants';

export type NavRole = 'reader' | 'author' | 'moderator' | 'admin';

export interface NavigationItem {
  /** Route path relative to /user/<username> (e.g., '/feed', '/profile'). */
  route: string;
  /** Human-readable label, used in the expanded drawer + tooltip + aria-label. */
  label: string;
  /** lucide-react icon component. */
  icon: LucideIcon;
  /** If set, the item only renders for users whose role matches one of these. */
  requiredRoles?: NavRole[];
  /** If true, the item only renders when VITE_ENABLE_VOUCHING === 'true'. */
  flagGated?: boolean;
}

/**
 * TRIL-126: central nav-items table for the desktop rail and future mobile drawer.
 * Order matters — items render top-to-bottom in this order.
 */
// TRIL-146: Profile + Settings are now reachable only via the AvatarMenu
// dropdown to avoid duplicate entry points. The rail covers content nav;
// the AvatarMenu covers user-identity nav.
export const NAVIGATION_ITEMS: ReadonlyArray<NavigationItem> = [
  { route: PRIVATE_URIS.FEED ?? '/feed', label: 'Feed', icon: Rss },
  // TRIL-327: the platform Pulse map (/pulse) — its only in-app entry point.
  { route: PRIVATE_URIS.PULSE ?? '/pulse', label: 'Pulse', icon: Activity },
  {
    route: PRIVATE_URIS.DRAFTS ?? '/drafts',
    label: 'Drafts',
    icon: PencilLine,
  },
  { route: PRIVATE_URIS.POSTS ?? '/posts', label: 'Posts', icon: FileText },
  {
    route: PRIVATE_URIS.REPLIES ?? '/replies',
    label: 'Replies',
    icon: MessageSquareReply,
  },
  {
    route: PRIVATE_URIS.STORIES ?? '/stories',
    label: 'Stories',
    icon: BookOpen,
  },
  { route: PRIVATE_URIS.LIKES ?? '/likes', label: 'Likes', icon: Heart },
  {
    route: PRIVATE_URIS.BOOKMARKED ?? '/bookmarked',
    label: 'Bookmarked',
    icon: Bookmark,
  },
  { route: PRIVATE_URIS.SHARED ?? '/shared', label: 'Shared', icon: Share2 },
  {
    route: PRIVATE_URIS.FOLLOWING ?? '/following',
    label: 'Following',
    icon: Users,
  },
  {
    route: PRIVATE_URIS.CIRCLES ?? '/circles',
    label: 'Circles',
    icon: CircleDot,
  },
  {
    route: PRIVATE_URIS.DIRECT_MESSAGES ?? '/direct-messages',
    label: 'Direct Messages',
    icon: MessageCircle,
  },
  {
    route: PRIVATE_URIS.VOUCH ?? '/vouch',
    label: 'Vouching',
    icon: Handshake,
    flagGated: true,
  },
  {
    route: PRIVATE_URIS.MODERATION_QUEUE ?? '/moderation/queue',
    label: 'Moderation',
    icon: ShieldCheck,
    requiredRoles: ['moderator', 'admin'],
  },
];

/**
 * Returns the filtered list of nav items for the given user role.
 * Items without `requiredRoles` are always returned.
 * Items with `flagGated` are only returned when VITE_ENABLE_VOUCHING === 'true'.
 */
export function visibleNavItems(
  role: NavRole | undefined | null,
): ReadonlyArray<NavigationItem> {
  const vouchingOn = import.meta.env.VITE_ENABLE_VOUCHING === 'true';
  return NAVIGATION_ITEMS.filter((item) => {
    if (item.flagGated && !vouchingOn) return false;
    if (!item.requiredRoles) return true;
    return role ? item.requiredRoles.includes(role) : false;
  });
}
